import React, { useMemo, useState } from 'react';
import Navbar from '../components/Navbar';
import HistorialVentasTable from '../components/cantina/historial/HistorialVentasTable';
import VentaDetalleModal from '../components/cantina/historial/VentaDetalleModal';
import AnularVentaModal from '../components/cantina/historial/AnularVentaModal';
import useCantinaHistorial from '../hooks/cantina/useCantinaHistorial';

const money = (value) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

export default function CantinaHistorial() {
  const historial = useCantinaHistorial();
  const [detalleAbierto, setDetalleAbierto] = useState(false);
  const [ventaAnular, setVentaAnular] = useState(null);
  const [anulando, setAnulando] = useState(false);

  const resumen = useMemo(() => {
    const ventas = (historial.ventas || []).filter(
      (venta) => venta.estado !== 'ANULADA'
    );

    const totalVendido = ventas.reduce(
      (total, venta) => total + Number(venta.total || 0),
      0
    );

    const unidades = ventas.reduce(
      (total, venta) =>
        total +
        (venta.items || []).reduce(
          (subtotal, item) =>
            subtotal + Number(item.cantidad || 0),
          0
        ),
      0
    );

    return {
      cantidad: ventas.length,
      totalVendido,
      unidades,
    };
  }, [historial.ventas]);

  const abrirDetalle = async (id) => {
    setDetalleAbierto(true);
    await historial.cargarDetalle(id);
  };

  const cerrarDetalle = () => {
    setDetalleAbierto(false);
    historial.cerrarDetalle();
  };

  const abrirAnulacion = (venta) => {
    setVentaAnular(venta);
  };

  const cerrarAnulacion = () => {
    if (anulando) return;
    setVentaAnular(null);
  };

  const confirmarAnulacion = async (data) => {
    try {
      setAnulando(true);

      const resultado = await historial.anularVenta(data);

      if (resultado?.ok) {
        setVentaAnular(null);
      }

      return resultado;
    } finally {
      setAnulando(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-azul-oscuro via-azul-oscuro to-verde-oscuro">
      <Navbar />

      <main className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-amarillo-dorado">
              Cantina
            </div>

            <h1 className="mt-1 text-3xl font-black text-blanco-puro">
              Historial de ventas
            </h1>

            <p className="mt-1 max-w-2xl text-sm text-blanco-puro/60">
              Consultá las ventas realizadas, quién las registró,
              cómo fueron cobradas y qué productos se vendieron.
            </p>
          </div>
        </div>

        <section className="mb-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-2xl border border-blanco-puro/15 bg-azul-oscuro/60 p-5 shadow-xl backdrop-blur">
            <div className="text-xs font-bold uppercase tracking-wide text-blanco-puro/50">
              Ventas
            </div>

            <div className="mt-2 text-3xl font-black text-blanco-puro">
              {resumen.cantidad}
            </div>
          </div>

          <div className="rounded-2xl border border-blanco-puro/15 bg-azul-oscuro/60 p-5 shadow-xl backdrop-blur">
            <div className="text-xs font-bold uppercase tracking-wide text-blanco-puro/50">
              Unidades vendidas
            </div>

            <div className="mt-2 text-3xl font-black text-blanco-puro">
              {resumen.unidades}
            </div>
          </div>

          <div className="rounded-2xl border border-amarillo-dorado/30 bg-amarillo-dorado/10 p-5 shadow-xl backdrop-blur">
            <div className="text-xs font-bold uppercase tracking-wide text-amarillo-dorado">
              Total vendido
            </div>

            <div className="mt-2 text-3xl font-black text-amarillo-dorado">
              {money(resumen.totalVendido)}
            </div>
          </div>
        </section>

        <section className="mb-5 rounded-2xl border border-blanco-puro/15 bg-azul-oscuro/60 p-4 shadow-xl backdrop-blur">
          <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto_auto] md:items-end">
            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
                Desde
              </span>

              <input
                type="date"
                value={historial.filtros.desde}
                onChange={(event) =>
                  historial.setFiltros((actual) => ({
                    ...actual,
                    desde: event.target.value,
                  }))
                }
                className="h-11 w-full rounded-xl border border-blanco-puro/20 bg-azul-oscuro/70 px-4 text-sm text-blanco-puro outline-none transition focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/30"
              />
            </label>

            <label className="block">
              <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-blanco-puro/60">
                Hasta
              </span>

              <input
                type="date"
                value={historial.filtros.hasta}
                onChange={(event) =>
                  historial.setFiltros((actual) => ({
                    ...actual,
                    hasta: event.target.value,
                  }))
                }
                className="h-11 w-full rounded-xl border border-blanco-puro/20 bg-azul-oscuro/70 px-4 text-sm text-blanco-puro outline-none transition focus:border-amarillo-dorado focus:ring-2 focus:ring-amarillo-dorado/30"
              />
            </label>

            <button
              type="button"
              onClick={historial.aplicarFiltros}
              disabled={historial.cargando}
              className="h-11 rounded-xl bg-amarillo-dorado px-5 text-sm font-bold text-azul-oscuro transition hover:bg-amber-400 disabled:opacity-50"
            >
              Aplicar
            </button>

            <button
              type="button"
              onClick={historial.limpiarFiltros}
              disabled={historial.cargando}
              className="h-11 rounded-xl border border-blanco-puro/20 px-5 text-sm font-bold text-blanco-puro transition hover:bg-blanco-puro/10 disabled:opacity-50"
            >
              Limpiar
            </button>
          </div>
        </section>

        {historial.error && (
          <div className="mb-5 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-100">
            {historial.error}
          </div>
        )}

        <HistorialVentasTable
          ventas={historial.ventas}
          cargando={historial.cargando}
          onDetalle={abrirDetalle}
        />
      </main>

      <VentaDetalleModal
        abierto={detalleAbierto}
        venta={historial.ventaDetalle}
        cargando={historial.cargandoDetalle}
        onClose={cerrarDetalle}
        onAnular={abrirAnulacion}
      />

      <AnularVentaModal
        abierto={Boolean(ventaAnular)}
        venta={ventaAnular}
        procesando={anulando}
        onClose={cerrarAnulacion}
        onConfirm={confirmarAnulacion}
      />
    </div>
  );
}
